import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import 'remixicon/fonts/remixicon.css'

export default function DataProtection() {
  const dataCollection = [
    {
      icon: 'ri-database-2-line',
      title: 'Order Processing',
      description: 'We collect information necessary to process and fulfill your food orders efficiently.',
      color: 'from-orange-400 to-orange-600'
    },
    {
      icon: 'ri-bank-card-line',
      title: 'Payment Verification',
      description: 'Payment details are collected to verify and process transactions securely.',
      color: 'from-green-400 to-green-600'
    },
    {
      icon: 'ri-user-follow-line',
      title: 'Delivery Coordination',
      description: 'Location and contact information to ensure accurate and timely delivery of your orders.',
      color: 'from-orange-400 to-orange-600'
    },
    {
      icon: 'ri-headphone-line',
      title: 'Customer Support',
      description: 'Contact details and order history to provide effective customer service and support.',
      color: 'from-green-400 to-green-600'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <div className="pt-20">
      <SEO 
        title="Data Protection & Privacy Policy - Your Information Security | Ami Bites"
        description="Learn how Ami Bites protects your personal data and privacy. Our commitment to secure payment processing, data encryption, and GDPR compliance for food delivery services."
        keywords="data protection, privacy policy, personal data security, GDPR compliance, secure payments, data privacy, information security, Ami Bites privacy"
        canonical="https://amibites.com/data-protection"
      />
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-green-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl mb-6"
            >
              <i className="ri-shield-check-line text-5xl text-white"></i>
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Data Protection <span className="text-gradient">& Privacy</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ami Bites is committed to protecting user information.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mb-16"
          >
            <h2 className="text-3xl font-bold mb-8 text-center">
              We Collect <span className="text-gradient">Personal Data For</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {dataCollection.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all group"
                >
                  <motion.div
                    initial={{ rotate: -180, scale: 0 }}
                    whileInView={{ rotate: 0, scale: 1 }}
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                    className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl mb-6`}
                  >
                    <i className={`${item.icon} text-3xl text-white`}></i>
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="grid md:grid-cols-2 gap-6 mb-16"
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <motion.div
                initial={{ rotate: -180, scale: 0 }}
                whileInView={{ rotate: 0, scale: 1 }}
                whileHover={{ rotate: 360, scale: 1.1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl mb-6"
              >
                <i className="ri-user-follow-line text-3xl text-white"></i>
              </motion.div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Third-Party Protection</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                We <strong>do not sell</strong> your personal information to third parties. Your data is used exclusively for providing and improving our services to you.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <motion.div
                initial={{ rotate: -180, scale: 0 }}
                whileInView={{ rotate: 0, scale: 1 }}
                whileHover={{ rotate: 360, scale: 1.1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl mb-6"
              >
                <i className="ri-lock-line text-3xl text-white"></i>
              </motion.div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Secure Transactions</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                All payment transactions are processed through <strong>secure, encrypted systems</strong> to protect your financial information at all times.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="bg-gradient-to-br from-orange-50 to-green-50 rounded-2xl p-8 border-l-4 border-orange-600"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <motion.div
                  initial={{ rotate: -180, scale: 0 }}
                  whileInView={{ rotate: 0, scale: 1 }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                  className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center"
                >
                  <i className="ri-delete-bin-line text-2xl text-white"></i>
                </motion.div>
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-2xl mb-3 text-gradient">Your Data Rights</h3>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  You have full control over your personal information. Users may request data updates or account deletion by contacting our support team.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="mailto:info@amibites.com" 
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-400 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-500 hover:to-orange-700 transition-all shadow-md hover:shadow-lg"
                  >
                    Contact Support
                  </a>
                  <a 
                    href="tel:+16838886864" 
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-400 to-green-600 text-white font-semibold rounded-lg hover:from-green-500 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
                  >
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="mt-8 text-center text-gray-500 text-sm"
          >
            <p>Last updated: February 2026</p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
